import {
  Button,
  ButtonVariant,
  IconIdentifier,
  Pill,
  PillVariant,
} from '@aus-platform/design-system';
import { isEmpty } from 'lodash';
import React, { useContext } from 'react';
import type { SSOEnforcementConfig } from 'shared/api';
import { useDashboardCategoryLabels, useOrganisationLabels } from 'shared/api';
import { GlobalContext } from 'shared/context';
import { FeatureFlag, SSOEnforcementType } from 'shared/enums';
import { isSsoLoginMode } from 'shared/helpers';
import { SSO_ENFORCEMENT_OPTIONS } from '../constants';
import { SingleLinePillList } from './single-line-pill-list';

type OrgControlsMainProps = {
  config?: SSOEnforcementConfig;
  onEditSsoEnforcement: VoidFunction;
  onEditDashboardConfigurations: VoidFunction;
  onEditIterationLabels: VoidFunction;
};

export const OrgControlsMain: React.FC<OrgControlsMainProps> = ({
  config,
  onEditSsoEnforcement,
  onEditDashboardConfigurations,
  onEditIterationLabels,
}) => {
  // Context.
  const { loggedUser } = useContext(GlobalContext);

  // Constants.
  const isSsoEnabled = isSsoLoginMode();

  const isProductivityDashboardEnabled = Boolean(
    loggedUser?.featureFlags[FeatureFlag.ProductivityDashboard],
  );

  // Apis.
  const { data: dashboardCategoryLabels } = useDashboardCategoryLabels(
    isProductivityDashboardEnabled,
  );

  const { data: labelsResponse, isLoading: isLoadingLabels } =
    useOrganisationLabels({ isActive: true });

  // Variables.
  const enforcementLabel = config
    ? (SSO_ENFORCEMENT_OPTIONS[config.ssoEnforcementType]?.label ?? 'None')
    : '';

  const domainCountLabel =
    config?.ssoEnforcementType === SSOEnforcementType.SelectedDomains
      ? `${config.ssoEnforcedDomains.length === 1 ? '1 domain' : `${config.ssoEnforcedDomains.length} domains`}`
      : '';

  const categoryLabels = dashboardCategoryLabels?.data?.labels;

  const dashboardEntriesLabel = categoryLabels
    ? Object.values(categoryLabels).join(', ')
    : 'No custom entries configured';

  const activeLabels = labelsResponse?.data?.labels ?? [];

  return (
    <div className="settings-content">
      <div className="settings-content__body org-controls-tab">
        <div className="org-controls-tab__header">
          <h3 className="org-controls-tab__header__title">
            Organisation Controls
          </h3>
          <p className="org-controls-tab__header__subtitle">
            Manage how users in your organisation sign in and access the
            platform.
          </p>
        </div>

        {isSsoEnabled && config && (
          <div className="org-controls-tab__card">
            <div className="org-controls-tab__card__header">
              <div>
                <h4 className="org-controls-tab__card__title">
                  Single Sign-On Enforcement
                </h4>
                <p className="org-controls-tab__card__description">
                  Control which users must use SSO to sign in, while others
                  use email and password.
                </p>
              </div>
              <Button
                variant={ButtonVariant.Outline}
                leftIconIdentifier={IconIdentifier.Pencil}
                iconSize={14}
                onClick={onEditSsoEnforcement}
              >
                Edit
              </Button>
            </div>

            <div className="org-controls-tab__card__enforced-on">
              <span className="org-controls-tab__card__enforced-on__label">
                Enforced on {domainCountLabel}
              </span>
              {config.ssoEnforcementType ===
              SSOEnforcementType.SelectedDomains ? (
                <div className="org-controls-tab__card__enforced-on__pills">
                  {config.ssoEnforcedDomains.map((domain) => (
                    <Pill key={domain} variant={PillVariant.Info}>
                      {domain}
                    </Pill>
                  ))}
                </div>
              ) : (
                <span className="org-controls-tab__card__enforced-on__value">
                  {enforcementLabel}
                </span>
              )}
            </div>
          </div>
        )}

        {isProductivityDashboardEnabled && (
          <div className="org-controls-tab__card">
            <div className="org-controls-tab__card__header">
              <div>
                <h4 className="org-controls-tab__card__title">
                  Dashboard Configurations
                </h4>
                <p className="org-controls-tab__card__description">
                  Manage your organisation’s dashboard settings and custom
                  config.
                </p>
              </div>
              <Button
                variant={ButtonVariant.Outline}
                leftIconIdentifier={IconIdentifier.Pencil}
                iconSize={14}
                onClick={onEditDashboardConfigurations}
              >
                Edit
              </Button>
            </div>

            <div className="org-controls-tab__card__details">
              <span className="org-controls-tab__card__details__label">
                Custom Entries
              </span>

              <span className="org-controls-tab__card__details__value">
                {dashboardEntriesLabel}
              </span>
            </div>
          </div>
        )}

        <div className="org-controls-tab__card">
          <div className="org-controls-tab__card__header">
            <div>
              <h4 className="org-controls-tab__card__title">
                Labels
              </h4>
              <p className="org-controls-tab__card__description">
                Manage custom labels available when adding or editing
                iterations.
              </p>
            </div>
            <Button
              variant={ButtonVariant.Outline}
              leftIconIdentifier={IconIdentifier.Pencil}
              iconSize={14}
              onClick={onEditIterationLabels}
            >
              Edit
            </Button>
          </div>

          {!isLoadingLabels && (
            <div className="org-controls-tab__card__details">
              <span className="org-controls-tab__card__details__label">
                Active Labels
              </span>

              {isEmpty(activeLabels) ? (
                <span className="org-controls-tab__card__details__value">
                  No labels configured
                </span>
              ) : (
                <SingleLinePillList items={activeLabels} />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
